import { Box, SxProps } from "@mui/material";

interface Props {
  text: string;
  limit?: number;
  highlightWords?: string[];
  sx?: SxProps;
}

type Text = { text: string, isHighlight: boolean };
type TextList = { textList: Text[], url?: string };
export default function SafeTextWithLinks({ text, limit, highlightWords, sx }: Props) {
  if (!text) return (<></>);

  const highlightRegex = highlightWords && highlightWords.length > 0 ? new RegExp(`(${highlightWords.join("|")})`, "gi") : undefined;
  const generateHighlightTextType = (str: string): Text[] => {
    if (!highlightRegex) {
      return [{text: str, isHighlight: false}];
    }
    const strList: Text[] = [];
    let lastIndex = 0;
    for (const match of Array.from(str.matchAll(highlightRegex))) {
      const highlight: string = match[0];
      const index: number = match.index ?? 0;

      strList.push({ text: str.slice(lastIndex, index), isHighlight: false });
      strList.push({ text: highlight, isHighlight: true });
      lastIndex = index + highlight.length;
    }
    if (lastIndex < str.length) {
      strList.push({ text: str.slice(lastIndex), isHighlight: false });
    }
    return strList
  }

  const orgLines = text.split(/\r\n|\n/);
  const totalLength = orgLines.reduce((acc: number, str: string) => acc + str.length, 0);
  const lines: TextList[][] = [];
  let length = 0;
  for (const line of orgLines) {
    const textList: TextList[] = [];
    let lastIndex = 0;
    for (const match of Array.from(line.matchAll(/\b(?:https?:\/\/)?(?:www\.[^\s]+|(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})[\S]*\b/g))) {
      const url: string = match[0];
      const urlStartIndex: number = match.index ?? 0;

      if (/^https?:\/\//i.test(url)) {
        // URL より前の文字列
        const str = line.slice(lastIndex, urlStartIndex);
        if (!limit || length + str.length < limit) {
          textList.push({ textList: generateHighlightTextType(str) });
          length += str.length;
          lastIndex += str.length;
        } else {
          const org = generateHighlightTextType(str);
          const modified: Text[] = [];
          for (const text of org) {
            if (limit && text.text.length + length >= limit) {
              modified.push({ text: text.text.slice(0, text.text.length - (length + text.text.length - limit)), isHighlight: text.isHighlight });
              break
            }
            modified.push({ text: text.text, isHighlight: text.isHighlight });
            length += text.text.length;
          }
          textList.push({ textList: modified });
          length = limit;
          if (length < totalLength) {
            textList.push({ textList: [{ text: "...", isHighlight: false }] });
          }
          lastIndex = line.length;
          break
        }

        // URL 部分
        if (!limit || length + url.length < limit) {
          textList.push({ textList: generateHighlightTextType(url), url: url });
          length += url.length;
          lastIndex += url.length;
        } else {
          const org = generateHighlightTextType(url);
          const modified: Text[] = [];
          for (const text of org) {
            if (limit && text.text.length + length >= limit) {
              modified.push({ text: text.text.slice(0, text.text.length - (length + text.text.length - limit)), isHighlight: text.isHighlight });
              break
            }
            modified.push({ text: text.text, isHighlight: text.isHighlight });
            length += text.text.length;
          }
          textList.push({ textList: modified, url: url });
          length = limit;
          if (length < totalLength) {
            textList.push({ textList: [{ text: "...", isHighlight: false }] });
          }
          lastIndex = line.length;
          break
        }
      } else {
        // http(s)が含まれていなければ通常の文字列として処理
        const str = line.slice(lastIndex, urlStartIndex) + url;
        if (!limit || length + str.length < limit) {
          textList.push({ textList: generateHighlightTextType(str) });
          length += str.length;
          lastIndex += str.length;
        } else {
          const org = generateHighlightTextType(str);
          const modified: Text[] = [];
          for (const text of org) {
            if (limit && text.text.length + length >= limit) {
              modified.push({ text: text.text.slice(0, text.text.length - (length + text.text.length - limit)), isHighlight: text.isHighlight });
              break
            }
            modified.push({ text: text.text, isHighlight: text.isHighlight });
            length += text.text.length;
          }
          textList.push({ textList: modified });
          length = limit;
          if (length < totalLength) {
            textList.push({ textList: [{ text: "...", isHighlight: false }] });
          }
          lastIndex = line.length;
          break
        }
      }
    }

    // URL より後ろの文字列
    if (lastIndex < line.length) {
      const str = line.slice(lastIndex);
      if (!limit || length + str.length < limit) {
        textList.push({ textList: generateHighlightTextType(str) });
        length += str.length;
      } else {
        const org = generateHighlightTextType(str);
        const modified: Text[] = [];
        for (const text of org) {
          if (limit && text.text.length + length >= limit) {
            modified.push({ text: text.text.slice(0, text.text.length - (length + text.text.length - limit)), isHighlight: text.isHighlight });
            break
          }
          modified.push({ text: text.text, isHighlight: text.isHighlight });
          length += text.text.length;
        }
        textList.push({ textList: modified });
        length = limit;
        if (length < totalLength) {
          textList.push({ textList: [{ text: "...", isHighlight: false }] });
        }
      }
    }
    lines.push(textList);
    if (limit && length >= limit) {
      break
    }
  };

  const textNode = (item: Text, index: number) => (
    item.isHighlight
      ? <span key={index} style={{ color: "red" }}>{item.text}</span>
      : <span key={index}>{item.text}</span>
  );

  return (
    <Box sx={sx}>
      {lines.map((line: TextList[], i: number) => {
        return (
          <Box key={i}>
            {line.map((textList: TextList, j: number) => {
              return textList.url ? (
                <a href={textList.url} target="_blank" rel="noopener noreferrer" key={j}>
                  {textList.textList.map((item: Text, k: number) => textNode(item, k))}
                </a>
              ) : (
                textList.textList.map((item: Text, k: number) => textNode(item, k))
              )
            })}
          </Box>
        )
      })}
    </Box>
  )
}
